import { Check, ImageOff, Loader2, Pencil, Plus, ShoppingBag, ShoppingCart, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { SearchPriceInput } from '@/components/form/search-price-input'
import { SearchProductInput } from '@/components/form/search-product-input'
import { useCreateUpsell, useUpdateUpsell, useUpsellsList } from '@/lib/api/upsells'
import { useProductDetails, useProduct } from '@/lib/api/products'
import { formatMoney } from '@/lib/money'
import { cn } from '@/lib/utils'
import React from 'react'
import type { RuntimeStepConfig } from '../types'

interface OrderBumpStepConfigProps {
  storeId: string
  value: RuntimeStepConfig['orderBumps']
  onChange: (config: RuntimeStepConfig['orderBumps']) => void
}

type OfferType = 'primary' | 'secondary' | 'vip' | 'non_interactive'

type InlineFormState = {
  title: string
  productId: string
  variantId: string
  priceId: string
  titleEn: string
  descriptionEn: string
  triggerType: 'any' | 'product'
  triggerProductId: string | null
  triggerProductName: string | null
  displayCompareAtPrice: boolean
  compareAtPriceDiscount: number
  offerType: OfferType
  precheck: boolean
}

const INITIAL_FORM: InlineFormState = {
  title: '',
  productId: '',
  variantId: '',
  priceId: '',
  titleEn: '',
  descriptionEn: '',
  triggerType: 'any',
  triggerProductId: null,
  triggerProductName: null,
  displayCompareAtPrice: false,
  compareAtPriceDiscount: 20,
  offerType: 'primary',
  precheck: false,
}

interface OfferData {
  id: string
  upsellId: string
  upsellTitle: string
  type: OfferType
  precheck: boolean
  productId: string | null
  variantId: string | null
  priceId: string | null
  titleEn: string
  descriptionEn: string
  triggers: { type: string; productId: string | null }[]
  rules: any[]
  displayCompareAtPrice: boolean
  compareAtPriceDiscount: number
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
    }).format(amount / 100)
  } catch {
    return `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`
  }
}

function buildTriggerLabel(triggers: { type: string; productId: string | null }[], productMap: Map<string, any>, t: (key: string, opts?: any) => string): string {
  if (!triggers || triggers.length === 0) return t('orderBump.alwaysShown')
  const productTrigger = triggers.find((t) => t.type === 'product' && t.productId)
  if (productTrigger?.productId) {
    const name = productMap.get(productTrigger.productId)?.name
    return name ? t('orderBump.whenCartHas', { name }) : t('orderBump.productTrigger')
  }
  return t('orderBump.alwaysShown')
}

function buildRulesBadge(rules: any[], t: (key: string, opts?: any) => string): string | null {
  if (!rules || rules.length === 0) return null
  const labels = rules.map((r: any) => {
    switch (r.type) {
      case 'CartContainsProduct': return t('orderBump.cartHasProduct')
      case 'CartTotalAbove': {
        const entry = r.minimumAmount ? Object.entries(r.minimumAmount)[0] : null
        return entry ? t('orderBump.cartAbove', { amount: formatCurrency((entry[1] as any).amount, entry[0]) }) : t('orderBump.cartTotalAbove')
      }
      case 'CartTotalBelow': {
        const entry = r.minimumAmount ? Object.entries(r.minimumAmount)[0] : null
        return entry ? t('orderBump.cartBelow', { amount: formatCurrency((entry[1] as any).amount, entry[0]) }) : t('orderBump.cartTotalBelow')
      }
      case 'CartItemCountAbove': return t('orderBump.itemsAbove', { count: r.itemCount })
      case 'CartItemCountBelow': return t('orderBump.itemsBelow', { count: r.itemCount })
      default: return null
    }
  }).filter(Boolean)
  return labels.length > 0 ? labels.join(' & ') : null
}

// ─── Offer Card ─────────────────────────────────────────────────────

function OfferCard({
  offer,
  isEnabled,
  showToggle,
  onToggle,
  onEdit,
  productData,
  triggerProductMap,
}: {
  offer: OfferData
  isEnabled: boolean
  showToggle: boolean
  onToggle: (enabled: boolean) => void
  onEdit: () => void
  productData?: any
  triggerProductMap: Map<string, any>
}) {
  const { t } = useTranslation('storefront')
  const product = productData
  const variant = product?.variants?.find((v: any) => v.id === offer.variantId)
  const imageUrl = variant?.imageUrl || null
  const productName = product?.name || t('orderBump.unknownProduct')
  const variantName = variant?.name || null

  const price = variant?.prices?.find((p: any) => p.id === offer.priceId)
  const firstCurrency = price?.currencyOptions
    ? Object.entries(price.currencyOptions)[0]
    : null
  const priceDisplay = firstCurrency
    ? formatCurrency((firstCurrency[1] as any).amount, firstCurrency[0])
    : null

  const triggerLabel = buildTriggerLabel(offer.triggers, triggerProductMap, t)
  const rulesBadge = buildRulesBadge(offer.rules, t)

  return (
    <div
      className={cn(
        'flex items-center gap-2.5 rounded-lg border px-3 py-2 transition-all',
        isEnabled
          ? 'border-border bg-card'
          : 'border-transparent bg-muted/30 opacity-60',
      )}
    >
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md border',
          isEnabled ? 'border-border bg-white dark:bg-slate-800' : 'border-transparent bg-muted/50',
        )}
      >
        {imageUrl ? (
          <img src={imageUrl} alt={productName} className="h-full w-full object-cover" />
        ) : (
          <ImageOff className="h-3.5 w-3.5 text-muted-foreground/40" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className={cn(
            'truncate text-xs font-medium',
            !isEnabled && 'text-muted-foreground',
          )}>
            {offer.upsellTitle}
          </span>
          {priceDisplay && (
            <Badge variant="secondary" className="shrink-0 text-[10px] font-semibold tabular-nums">
              {priceDisplay}
            </Badge>
          )}
        </div>
        <p className="truncate text-[10px] text-muted-foreground">
          {productName}
          {variantName && variantName !== productName ? ` · ${variantName}` : ''}
        </p>
        <div className="flex items-center gap-1 pt-0.5">
          <Badge variant="outline" className="h-4 border-muted-foreground/20 px-1.5 text-[9px] font-normal text-muted-foreground">
            {triggerLabel}
          </Badge>
          {rulesBadge && (
            <Badge variant="outline" className="h-4 border-amber-300/50 bg-amber-50 px-1.5 text-[9px] font-normal text-amber-700 dark:border-amber-600/30 dark:bg-amber-950/30 dark:text-amber-400">
              {rulesBadge}
            </Badge>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={onEdit}
          className="rounded-md p-1 text-muted-foreground/50 transition-colors hover:bg-muted hover:text-foreground"
        >
          <Pencil className="h-3 w-3" />
        </button>
        {showToggle && (
          <Switch
            checked={isEnabled}
            onCheckedChange={onToggle}
            className="shrink-0 scale-90"
          />
        )}
      </div>
    </div>
  )
}

// ─── Edit Form ──────────────────────────────────────────────────────

type EditFormState = {
  upsellId: string
  title: string
  productId: string
  variantId: string
  priceId: string
  titleEn: string
  descriptionEn: string
  triggerType: 'any' | 'product'
  triggerProductId: string | null
  triggerProductName: string | null
  displayCompareAtPrice: boolean
  compareAtPriceDiscount: number
  offerType: OfferType
  precheck: boolean
}

function buildEditFormFromOffer(offer: OfferData, productMap: Map<string, any>): EditFormState {
  const triggerType = offer.triggers?.some((t) => t.type === 'product' && t.productId) ? 'product' as const : 'any' as const
  const triggerProduct = offer.triggers?.find((t) => t.type === 'product' && t.productId)
  return {
    upsellId: offer.upsellId,
    title: offer.upsellTitle,
    productId: offer.productId ?? '',
    variantId: offer.variantId ?? '',
    priceId: offer.priceId ?? '',
    titleEn: offer.titleEn,
    descriptionEn: offer.descriptionEn,
    triggerType,
    triggerProductId: triggerProduct?.productId ?? null,
    triggerProductName: triggerProduct?.productId ? (productMap.get(triggerProduct.productId)?.name ?? null) : null,
    displayCompareAtPrice: offer.displayCompareAtPrice,
    compareAtPriceDiscount: offer.compareAtPriceDiscount,
    offerType: offer.type,
    precheck: offer.precheck,
  }
}

// ─── Shared inline form fields ──────────────────────────────────────

function BumpFormFields({
  form,
  setForm,
  storeId,
  autoFocusTitle,
}: {
  form: InlineFormState | EditFormState
  setForm: (updater: (prev: any) => any) => void
  storeId: string
  autoFocusTitle?: boolean
}) {
  const { t } = useTranslation('storefront')
  const { data: selectedProduct } = useProduct(form.productId, !!form.productId)

  const availablePrices = React.useMemo(() => {
    if (!selectedProduct?.variants) return []
    return selectedProduct.variants.flatMap((v: any) =>
      (v.prices ?? []).map((p: any) => ({ ...p, variantId: v.id, variantName: v.name })),
    )
  }, [selectedProduct])

  const showPriceSelector = availablePrices.length > 1 && !!form.productId

  return (
    <>
      <div className="space-y-1">
        <Label className="text-xs">{t('orderBump.bumpName')}</Label>
        <Input
          value={form.title}
          onChange={(e) => setForm((f: any) => ({ ...f, title: e.target.value }))}
          placeholder={t('orderBump.bumpNamePlaceholder')}
          className="h-8 text-sm"
          autoFocus={autoFocusTitle}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs">{t('orderBump.productAndPrice')}</Label>
        <SearchPriceInput
          storeId={storeId}
          priceId={form.priceId || undefined}
          includeInactive
          onChange={(selection) => {
            setForm((f: any) => ({
              ...f,
              productId: selection.productId,
              variantId: selection.variantId,
              priceId: selection.priceId,
            }))
          }}
        />
      </div>

      {showPriceSelector && (
        <div className="space-y-1">
          <Label className="text-xs">{t('orderBump.price')}</Label>
          <Select
            value={form.priceId}
            onValueChange={(newPriceId) => {
              const selected = availablePrices.find((p: any) => p.id === newPriceId)
              if (selected) {
                setForm((f: any) => ({
                  ...f,
                  priceId: newPriceId,
                  variantId: selected.variantId,
                }))
              }
            }}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availablePrices.map((p: any) => {
                const co = p.currencyOptions
                const cur = co ? Object.keys(co)[0] : null
                const amt = cur && co?.[cur] ? co[cur].amount : 0
                const recurring = p.recurring && p.interval ? ` / ${p.interval}` : ''
                const label = `${p.variantName} · ${cur ? formatMoney(amt, cur.toUpperCase()) : 'N/A'}${recurring}`
                return (
                  <SelectItem key={p.id} value={p.id} className="text-sm">
                    {label}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-1">
        <Label className="text-xs">{t('orderBump.offerType')}</Label>
        <Select
          value={form.offerType}
          onValueChange={(v: OfferType) => {
            setForm((f: any) => ({
              ...f,
              offerType: v,
              precheck: v === 'non_interactive' ? true : f.precheck,
            }))
          }}
        >
          <SelectTrigger className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="primary">{t('orderBump.offerTypes.primary')}</SelectItem>
            <SelectItem value="secondary">{t('orderBump.offerTypes.secondary')}</SelectItem>
            <SelectItem value="vip">{t('orderBump.offerTypes.vip')}</SelectItem>
            <SelectItem value="non_interactive">{t('orderBump.offerTypes.nonInteractive')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {form.offerType !== 'non_interactive' && (
        <div className="flex items-center justify-between gap-2">
          <div className="space-y-0.5">
            <Label className="text-xs">{t('orderBump.precheck')}</Label>
            <p className="text-[11px] leading-tight text-muted-foreground">
              {t('orderBump.precheckDescription')}
            </p>
          </div>
          <Switch
            checked={form.precheck}
            onCheckedChange={(checked) =>
              setForm((f: any) => ({ ...f, precheck: checked }))
            }
          />
        </div>
      )}

      <div className="space-y-1">
        <Label className="text-xs">{t('orderBump.customerFacingTitle')} <span className="text-muted-foreground">({t('orderBump.optional')})</span></Label>
        <Input
          value={form.titleEn}
          onChange={(e) => setForm((f: any) => ({ ...f, titleEn: e.target.value }))}
          placeholder={t('orderBump.customerFacingTitlePlaceholder')}
          className="h-8 text-sm"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs">{t('orderBump.description')} <span className="text-muted-foreground">({t('orderBump.optional')})</span></Label>
        <Input
          value={form.descriptionEn}
          onChange={(e) => setForm((f: any) => ({ ...f, descriptionEn: e.target.value }))}
          placeholder={t('orderBump.descriptionPlaceholder')}
          className="h-8 text-sm"
        />
      </div>

      <div className="space-y-2 rounded-md border bg-muted/20 px-2.5 py-2">
        <div className="flex items-center justify-between gap-2">
          <div className="space-y-0.5">
            <Label className="text-xs">{t('orderBump.showCompareAtPrice')}</Label>
            <p className="text-[11px] leading-tight text-muted-foreground">
              {t('orderBump.showCompareAtPriceDesc')}
            </p>
          </div>
          <Switch
            checked={form.displayCompareAtPrice}
            onCheckedChange={(checked) =>
              setForm((f: any) => ({ ...f, displayCompareAtPrice: checked }))
            }
          />
        </div>
        {form.displayCompareAtPrice && (
          <div className="space-y-1">
            <Label className="text-xs">{t('orderBump.discountPercent')}</Label>
            <div className="relative">
              <Input
                type="number"
                min={1}
                max={99}
                step={1}
                value={form.compareAtPriceDiscount}
                onChange={(e) => {
                  const raw = Number(e.target.value)
                  const clamped = Number.isFinite(raw) ? Math.min(99, Math.max(1, Math.round(raw))) : 20
                  setForm((f: any) => ({ ...f, compareAtPriceDiscount: clamped }))
                }}
                className="h-8 pr-7 text-sm"
              />
              <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-muted-foreground">
                %
              </span>
            </div>
            <p className="text-[11px] leading-tight text-muted-foreground">
              {t('orderBump.discountExplanation', { percent: 100 - form.compareAtPriceDiscount })}
            </p>
          </div>
        )}
      </div>

      <div className="space-y-1">
        <Label className="text-xs">{t('orderBump.showWhen')}</Label>
        <Select
          value={form.triggerType}
          onValueChange={(v: 'any' | 'product') => {
            setForm((f: any) => ({
              ...f,
              triggerType: v,
              triggerProductId: v === 'any' ? null : f.triggerProductId,
              triggerProductName: v === 'any' ? null : f.triggerProductName,
            }))
          }}
        >
          <SelectTrigger className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{t('orderBump.alwaysAnyCart')}</SelectItem>
            <SelectItem value="product">{t('orderBump.cartContainsProduct')}</SelectItem>
          </SelectContent>
        </Select>
        {form.triggerType === 'product' && (
          <div className="pt-1">
            {form.triggerProductName ? (
              <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-2.5 py-1.5">
                <ShoppingCart className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate text-xs">{form.triggerProductName}</span>
                <button
                  type="button"
                  onClick={() => setForm((f: any) => ({ ...f, triggerProductId: null, triggerProductName: null }))}
                  className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <SearchProductInput
                storeId={storeId}
                onSelect={(product) => {
                  setForm((f: any) => ({
                    ...f,
                    triggerProductId: product.id,
                    triggerProductName: product.name,
                  }))
                }}
              />
            )}
          </div>
        )}
      </div>
    </>
  )
}

// ─── Main Component ─────────────────────────────────────────────────

export function OrderBumpStepConfig({
  storeId,
  value,
  onChange,
}: OrderBumpStepConfigProps) {
  const { t } = useTranslation('storefront')
  const { data, isLoading } = useUpsellsList(
    { storeId, type: 'orderbump' },
    { enabled: !!storeId },
  )

  const createMutation = useCreateUpsell()
  const updateMutation = useUpdateUpsell()

  const [showInlineForm, setShowInlineForm] = React.useState(false)
  const [inlineForm, setInlineForm] = React.useState<InlineFormState>(INITIAL_FORM)
  const [editingOfferId, setEditingOfferId] = React.useState<string | null>(null)
  const [editForm, setEditForm] = React.useState<EditFormState | null>(null)

  const mode = value?.mode ?? 'inherit'
  const enabledOfferIds = value?.enabledOfferIds ?? []

  const allUpsells = data?.upsells ?? []
  const orderBumpOffers: OfferData[] = allUpsells
    .filter((u) => u.enabled)
    .flatMap((upsell) =>
      (upsell.orderBumpOffers ?? []).map((offer) => ({
        id: offer.id,
        upsellId: upsell.id,
        upsellTitle: upsell.offerTitle,
        type: (offer.type as OfferType) ?? 'primary',
        precheck: (offer as any).precheck ?? false,
        productId: offer.productId,
        variantId: offer.variantId ?? null,
        priceId: (offer as any).priceId ?? null,
        titleEn: (offer as any).titleTrans?.en || '',
        descriptionEn: (offer as any).descriptionTrans?.en || '',
        triggers: (upsell as any).triggers ?? [],
        rules: (offer as any).rules ?? [],
        displayCompareAtPrice: (offer as any).displayCompareAtPrice ?? false,
        compareAtPriceDiscount: (offer as any).compareAtPriceDiscount ?? 20,
      })),
    )

  const productIds = React.useMemo(() => {
    const ids = new Set<string>()
    orderBumpOffers.forEach((o) => {
      if (o.productId) ids.add(o.productId)
      o.triggers?.forEach((t) => { if (t.productId) ids.add(t.productId) })
    })
    return [...ids]
  }, [orderBumpOffers])
  const { data: productsData } = useProductDetails(productIds, productIds.length > 0)
  const productMap = React.useMemo(() => {
    const map = new Map<string, any>()
    productsData?.forEach((p: any) => map.set(p.id, p))
    return map
  }, [productsData])

  const handleModeChange = (newMode: string) => {
    if (newMode === 'inherit') {
      onChange({ mode: 'inherit' })
    } else {
      onChange({
        mode: 'custom',
        enabledOfferIds: orderBumpOffers.map((o) => o.id),
      })
    }
  }

  const handleToggleOffer = (offerId: string, enabled: boolean) => {
    const currentIds = new Set(enabledOfferIds)
    if (enabled) {
      currentIds.add(offerId)
    } else {
      currentIds.delete(offerId)
    }
    onChange({
      mode: 'custom',
      enabledOfferIds: Array.from(currentIds),
    })
  }

  const handleInlineCreate = async () => {
    if (!inlineForm.title.trim() || !inlineForm.priceId) return
    if (inlineForm.triggerType === 'product' && !inlineForm.triggerProductId) return

    const triggers = inlineForm.triggerType === 'product'
      ? [{ productId: inlineForm.triggerProductId, type: 'product' as const }]
      : [{ productId: null, type: 'any' as const }]

    try {
      await createMutation.mutateAsync({
        storeId,
        offerTitle: inlineForm.title.trim(),
        enabled: true,
        type: 'orderbump',
        triggers,
        offers: [],
        orderBumpOffers: [{
          productId: inlineForm.productId,
          variantId: inlineForm.variantId || null,
          priceId: inlineForm.priceId || null,
          type: inlineForm.offerType,
          precheck: inlineForm.precheck,
          quantity: 1,
          titleTrans: inlineForm.titleEn ? { en: inlineForm.titleEn } : {},
          descriptionTrans: inlineForm.descriptionEn ? { en: inlineForm.descriptionEn } : {},
          overrideImageUrl: null,
          displayPrice: true,
          displayCompareAtPrice: inlineForm.displayCompareAtPrice,
          compareAtPriceDiscount: inlineForm.compareAtPriceDiscount,
          rules: [],
        }],
      })

      setInlineForm(INITIAL_FORM)
      setShowInlineForm(false)

      if (mode === 'custom') {
        onChange({ mode: 'inherit' })
      }
    } catch {
      // Error handled by the mutation hook (toast)
    }
  }

  const handleStartEdit = (offer: OfferData) => {
    setEditingOfferId(offer.id)
    setEditForm(buildEditFormFromOffer(offer, productMap))
    setShowInlineForm(false)
  }

  const handleCancelEdit = () => {
    setEditingOfferId(null)
    setEditForm(null)
  }

  const handleSaveEdit = async () => {
    if (!editForm || !editingOfferId) return
    if (!editForm.title.trim() || !editForm.priceId) return
    if (editForm.triggerType === 'product' && !editForm.triggerProductId) return

    const upsell = allUpsells.find((u) => u.id === editForm.upsellId)
    if (!upsell) return

    const triggers = editForm.triggerType === 'product'
      ? [{ productId: editForm.triggerProductId, type: 'product' as const }]
      : [{ productId: null, type: 'any' as const }]

    try {
      await updateMutation.mutateAsync({
        id: editForm.upsellId,
        data: {
          offerTitle: editForm.title.trim(),
          enabled: upsell.enabled ?? true,
          type: 'orderbump',
          triggers,
          offers: [],
          orderBumpOffers: (upsell.orderBumpOffers ?? []).map((existingOffer: any) => {
            if (existingOffer.id === editingOfferId) {
              return {
                id: existingOffer.id,
                productId: editForm.productId,
                variantId: editForm.variantId || null,
                priceId: editForm.priceId || null,
                type: editForm.offerType,
                precheck: editForm.precheck,
                quantity: existingOffer.quantity || 1,
                titleTrans: editForm.titleEn ? { ...existingOffer.titleTrans, en: editForm.titleEn } : (existingOffer.titleTrans || {}),
                descriptionTrans: editForm.descriptionEn ? { ...existingOffer.descriptionTrans, en: editForm.descriptionEn } : (existingOffer.descriptionTrans || {}),
                overrideImageUrl: existingOffer.overrideImageUrl ?? null,
                displayPrice: existingOffer.displayPrice ?? true,
                displayCompareAtPrice: editForm.displayCompareAtPrice,
                compareAtPriceDiscount: editForm.compareAtPriceDiscount,
                rules: existingOffer.rules ?? [],
              }
            }
            return {
              id: existingOffer.id,
              productId: existingOffer.productId,
              variantId: existingOffer.variantId ?? null,
              priceId: existingOffer.priceId ?? null,
              type: existingOffer.type,
              precheck: existingOffer.precheck,
              quantity: existingOffer.quantity || 1,
              titleTrans: existingOffer.titleTrans || {},
              descriptionTrans: existingOffer.descriptionTrans || {},
              overrideImageUrl: existingOffer.overrideImageUrl ?? null,
              displayPrice: existingOffer.displayPrice ?? true,
              displayCompareAtPrice: existingOffer.displayCompareAtPrice ?? false,
              compareAtPriceDiscount: existingOffer.compareAtPriceDiscount ?? 20,
              rules: existingOffer.rules ?? [],
            }
          }),
        },
      })
      handleCancelEdit()
    } catch {
      // Error handled by the mutation hook (toast)
    }
  }

  const isEditFormValid = editForm && editForm.title.trim() && editForm.priceId
    && (editForm.triggerType === 'any' || editForm.triggerProductId)

  const isFormValid = inlineForm.title.trim() && inlineForm.priceId
    && (inlineForm.triggerType === 'any' || inlineForm.triggerProductId)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-2.5">
      {/* Mode selector */}
      {orderBumpOffers.length > 0 && (
        <RadioGroup
          value={mode}
          onValueChange={handleModeChange}
          className="flex items-center gap-4"
        >
          <div className="flex items-center gap-1.5">
            <RadioGroupItem value="inherit" id="ob-inherit" className="h-3.5 w-3.5" />
            <Label htmlFor="ob-inherit" className="cursor-pointer text-xs">
              {t('orderBump.allBumps')}
            </Label>
            <Badge variant="secondary" className="text-[10px]">
              {orderBumpOffers.length}
            </Badge>
          </div>
          <div className="flex items-center gap-1.5">
            <RadioGroupItem value="custom" id="ob-custom" className="h-3.5 w-3.5" />
            <Label htmlFor="ob-custom" className="cursor-pointer text-xs">
              {t('orderBump.pickForThisStep')}
            </Label>
            {mode === 'custom' && (
              <Badge variant="secondary" className="text-[10px]">
                {enabledOfferIds.length}/{orderBumpOffers.length}
              </Badge>
            )}
          </div>
        </RadioGroup>
      )}

      {/* Offer cards */}
      {orderBumpOffers.length > 0 ? (
        <div className={cn(
          'space-y-1 overflow-y-auto',
          orderBumpOffers.length > 4 && 'max-h-[280px]',
        )}>
          {orderBumpOffers.map((offer) => {
            const isEnabled = mode === 'inherit' || enabledOfferIds.includes(offer.id)

            if (editingOfferId === offer.id && editForm) {
              return (
                <div key={offer.id} className="space-y-2.5 rounded-lg border border-primary/30 bg-primary/5 p-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-semibold uppercase tracking-wide">{t('orderBump.editOrderBump')}</h4>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  <BumpFormFields
                    form={editForm}
                    setForm={setEditForm}
                    storeId={storeId}
                    autoFocusTitle
                  />

                  <div className="flex items-center gap-2 pt-0.5">
                    <Button
                      type="button"
                      size="sm"
                      className="h-7 gap-1.5 text-xs"
                      disabled={!isEditFormValid || updateMutation.isPending}
                      onClick={handleSaveEdit}
                    >
                      {updateMutation.isPending ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Check className="h-3 w-3" />
                      )}
                      {updateMutation.isPending ? t('orderBump.saving') : t('orderBump.save')}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={handleCancelEdit}
                    >
                      {t('orderBump.cancel')}
                    </Button>
                  </div>
                </div>
              )
            }

            return (
              <OfferCard
                key={offer.id}
                offer={offer}
                isEnabled={isEnabled}
                showToggle={mode === 'custom'}
                onToggle={(checked) => handleToggleOffer(offer.id, checked)}
                onEdit={() => handleStartEdit(offer)}
                productData={offer.productId ? productMap.get(offer.productId) : undefined}
                triggerProductMap={productMap}
              />
            )
          })}
        </div>
      ) : (
        !showInlineForm && (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed py-6 text-center">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
              <ShoppingBag className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <p className="text-xs font-medium">{t('orderBump.noOrderBumpsYet')}</p>
              <p className="text-[10px] text-muted-foreground">
                {t('orderBump.increaseAov')}
              </p>
            </div>
          </div>
        )
      )}

      {/* Inline create form */}
      {showInlineForm ? (
        <div className="space-y-2.5 rounded-lg border border-primary/30 bg-primary/5 p-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold uppercase tracking-wide">{t('orderBump.newOrderBump')}</h4>
            <button
              type="button"
              onClick={() => { setShowInlineForm(false); setInlineForm(INITIAL_FORM) }}
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <BumpFormFields
            form={inlineForm}
            setForm={setInlineForm}
            storeId={storeId}
            autoFocusTitle
          />

          <div className="flex items-center gap-2 pt-0.5">
            <Button
              type="button"
              size="sm"
              className="h-7 gap-1.5 text-xs"
              disabled={!isFormValid || createMutation.isPending}
              onClick={handleInlineCreate}
            >
              {createMutation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Check className="h-3 w-3" />
              )}
              {createMutation.isPending ? t('orderBump.creating') : t('orderBump.createAndEnable')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => { setShowInlineForm(false); setInlineForm(INITIAL_FORM) }}
            >
              {t('orderBump.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full gap-1.5 h-8 text-xs"
          onClick={() => setShowInlineForm(true)}
        >
          <Plus className="h-3 w-3" />
          {t('orderBump.newOrderBump')}
        </Button>
      )}
    </div>
  )
}
